import type { TypeTab } from 'src/types/Tabs';

import { showNewLayout } from './new_layout';
import {
	ALL_ENERGY_WIDGETS_DASHBOARD_TAB,
	ENERGY_DASHBOARD_ID,
	ENERGY_MONITORING_DASHBOARD_TAB,
} from 'src/components/EnergyMonitoring/helpers/constants';
import {
	applyEnergyDashboardVisualToTab,
	getEnergyDashboardVisualSettings,
} from 'src/components/EnergyMonitoring/helpers/energy-dashboard-settings';
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { isDevModalAvailable } from 'src/helpers/dev_tools';
import { user_data } from 'src/stores/account/user';
import { isAllEnergyWidgetsGalleryEnabled } from 'src/stores/features/dev/all-energy-widgets-gallery';
import { isAllDeviceWidgetsGalleryEnabled } from 'src/stores/features/dev/all-device-widgets-gallery';
import { isMockLightsGalleryEnabled } from 'src/stores/features/dev/mock-lights-gallery';
import { ALL_DEVICE_WIDGETS_DASHBOARD_TAB } from 'src/components/Dashboards/all-device-widgets';
import { MOCK_LIGHTS_DASHBOARD_TAB } from 'src/components/Dashboards/mock-lights-gallery';

/** @deprecated Legacy dashboard tab — new layout skips activity log tab. */
export const dashboardActivityLogTab: TypeTab = {
	id: -10,
	key: 'event_log',
	text: 'tab_activity_log',
	defaultText: 'activity log',
	route: '/event_log',
};

const dashboardCameraMonitoringTab: TypeTab = {
	id: -11,
	key: 'camera_monitoring',
	text: 'tab_camera_monitoring',
	defaultText: 'Camera Monitoring',
	route: '/camera_monitoring',
	icon: 'fas fa-video',
};

const dashboardEnergyMonitoringTab: TypeTab = {
	id: ENERGY_MONITORING_DASHBOARD_TAB.id,
	key: ENERGY_MONITORING_DASHBOARD_TAB.key,
	text: ENERGY_MONITORING_DASHBOARD_TAB.text,
	defaultText: ENERGY_MONITORING_DASHBOARD_TAB.defaultText,
	route: ENERGY_MONITORING_DASHBOARD_TAB.route,
	icon: ENERGY_MONITORING_DASHBOARD_TAB.icon,
	backgroundColor: ENERGY_MONITORING_DASHBOARD_TAB.backgroundColor,
};

const localizeTab = (tab: TypeTab): TypeTab => ({
	...tab,
	text: get(format)(tab.text, { default: tab.defaultText ?? tab.text }),
});

export type SystemDashboardTabsOptions = {
	showEnergy?: boolean;
	home_data?: Record<string, unknown> | null;
};

/** Prepends system dashboards (camera, energy) and activity log on legacy layout. */
export const getSystemDashboardTabs = <T>(
	dashboardTabsSort: T[],
	options: SystemDashboardTabsOptions = {},
): (TypeTab | T)[] => {
	const { showEnergy = false, home_data = null } = options;

	const cameraMonitoringTab = localizeTab(dashboardCameraMonitoringTab);
	const energyTabs: TypeTab[] = showEnergy
		? [applyEnergyDashboardVisualToTab(localizeTab(dashboardEnergyMonitoringTab), home_data)]
		: [];
	const sharedEnergyVisual = getEnergyDashboardVisualSettings(ENERGY_DASHBOARD_ID, home_data);
	const allEnergyWidgetsTabs: TypeTab[] =
		showEnergy && isDevModalAvailable(get(user_data)?.email) && isAllEnergyWidgetsGalleryEnabled()
			? [
					{
						...ALL_ENERGY_WIDGETS_DASHBOARD_TAB,
						backgroundColor: sharedEnergyVisual.backgroundColor,
					},
				]
			: [];
	const allDeviceWidgetsTabs: TypeTab[] =
		isDevModalAvailable(get(user_data)?.email) && isAllDeviceWidgetsGalleryEnabled()
			? [{ ...ALL_DEVICE_WIDGETS_DASHBOARD_TAB }]
			: [];
	const mockLightsGalleryTabs: TypeTab[] =
		isDevModalAvailable(get(user_data)?.email) && isMockLightsGalleryEnabled()
			? [{ ...MOCK_LIGHTS_DASHBOARD_TAB }]
			: [];

	if (get(showNewLayout)) {
		return [
			cameraMonitoringTab,
			...energyTabs,
			...allEnergyWidgetsTabs,
			...allDeviceWidgetsTabs,
			...mockLightsGalleryTabs,
			...dashboardTabsSort,
		];
	}

	return [
		dashboardActivityLogTab,
		cameraMonitoringTab,
		...energyTabs,
		...allEnergyWidgetsTabs,
		...allDeviceWidgetsTabs,
		...mockLightsGalleryTabs,
		...dashboardTabsSort,
	];
};

/** @deprecated Use getSystemDashboardTabs. */
export const getCameraDashboardTabs = <T>(
	dashboardTabsSort: T[],
	options?: SystemDashboardTabsOptions,
) => getSystemDashboardTabs(dashboardTabsSort, options);

/** Prepends activity log tab on legacy layout only. */
export const prependLegacyActivityLogTab = <T>(dashboardTabsSort: T[]) => {
	if (get(showNewLayout)) {
		return [...dashboardTabsSort];
	}
	return [dashboardActivityLogTab, ...dashboardTabsSort];
};
